<template>
  <div>
    <!-- Initial Loading Overlay -->
    <div v-if="initialLoading" class="ts-fixed ts-inset-0 ts-bg-white ts-bg-opacity-90 ts-flex ts-items-center ts-justify-center" style="z-index: 9999;">
      <div class="ts-text-center">
        <a-spin size="large" />
        <p class="ts-mt-4 ts-text-gray-600">{{ $t('classOperationV2.loadingInitialData') || 'Loading initial data...' }}</p>
      </div>
    </div>

    <!-- Header & Breadcrumb -->
    <div class="ts-flex ts-justify-between ts-items-center">
      <div class="ts-flex ts-items-center ts-gap-3">
        <a-tooltip :title="$t('classOperationV2.backToList')" placement="right">
          <a-button
            @click="goBackToList"
            shape="circle"
            icon="arrow-left"
            class="back-button"
          />
        </a-tooltip>
        <h1 class="page-header ts-mb-0">
          {{ $t('classOperationV2.batchCreation') }}
        </h1>
      </div>
      <ts-breadcrumb :items="[
        { text: $t('toHomePage'), href: '/' },
        { text: $t('classOperationV2.pageTitle'), href: '/class-operations' },
        { text: $t('classOperationV2.batchCreation'), active: true }
      ]" />
    </div>

    <!-- Main Card -->
    <ts-card class="ts-mt-4">
      <ts-card-wrapper>
        <!-- Filter Section -->
        <div class="ts-mb-6">
          <!-- Header with Icon -->
          <div class="ts-flex ts-items-center ts-mb-4">
            <div class="ts-w-8 ts-h-8 ts-rounded ts-bg-blue-100 ts-flex ts-items-center ts-justify-center">
              <i class="fas fa-filter ts-text-blue-500 ts-text-sm"></i>
            </div>
            <div class="ts-ml-3">
              <h5 class="ts-text-base ts-font-semibold ts-text-gray-800 ts-m-0">{{ $t('classOperationV2.selectSourceClasses') }}</h5>
              <p class="ts-text-xs ts-text-gray-500 ts-m-0">{{ $t('classOperationV2.filterDescription') }}</p>
            </div>
          </div>

          <!-- Filter Card -->
          <div class="ts-bg-white ts-rounded ts-border ts-border-gray-200 ts-p-4">
            <!-- Filters Grid -->
            <div class="ts-grid ts-grid-cols-1 md:ts-grid-cols-2 lg:ts-grid-cols-4 ts-gap-3 ts-mb-3">
              <!-- Main Program -->
              <div class="ts-relative">
                <label class="ts-block ts-text-xs ts-font-medium ts-text-gray-700 ts-mb-1">
                  {{ $t('classOperationV2.mainProgram') }} <span class="ts-text-red-500">*</span>
                </label>
                <a-select
                  v-model="filters.mainProgramId"
                  class="ts-w-full"
                  :placeholder="$t('classOperationV2.select')"
                  show-search
                  @change="onMainProgramChange"
                >
                  <a-select-option v-for="program in mainPrograms" :key="program.value" :value="program.value">
                    {{ program.label }}
                  </a-select-option>
                </a-select>
              </div>

              <!-- Sub Program -->
              <div class="ts-relative">
                <label class="ts-block ts-text-xs ts-font-medium ts-text-gray-700 ts-mb-1">
                  {{ $t('classOperationV2.subProgram') }} <span class="ts-text-red-500">*</span>
                </label>
                <a-select
                  v-model="filters.subProgramId"
                  class="ts-w-full"
                  :placeholder="$t('classOperationV2.select')"
                  show-search
                  :disabled="!filters.mainProgramId || loadingSubPrograms"
                  :loading="loadingSubPrograms"
                  @change="onSubProgramChange"
                >
                  <a-select-option v-for="program in subPrograms" :key="program.value" :value="program.value">
                    {{ program.label }}
                  </a-select-option>
                </a-select>
              </div>

              <!-- Academic Status -->
              <div class="ts-relative">
                <label class="ts-block ts-text-xs ts-font-medium ts-text-gray-700 ts-mb-1">
                  {{ $t('classOperationV2.academicStatus') }} <span class="ts-text-red-500">*</span>
                </label>
                <a-select
                  v-model="filters.academicStatusId"
                  class="ts-w-full"
                  :placeholder="$t('classOperationV2.select')"
                  :disabled="!filters.subProgramId"
                  @change="onStatusChange"
                >
                  <a-select-option :value="1">
                    {{ $t('classOperationV2.statusCurrent') }}
                  </a-select-option>
                  <a-select-option :value="3">
                    {{ $t('classOperationV2.statusNext') }}
                  </a-select-option>
                </a-select>
              </div>

              <!-- Academic Year -->
              <div class="ts-relative">
                <label class="ts-block ts-text-xs ts-font-medium ts-text-gray-700 ts-mb-1">
                  {{ $t('classOperationV2.academicYear') }} <span class="ts-text-red-500">*</span>
                </label>
                <a-select
                  v-model="filters.academicYearId"
                  class="ts-w-full"
                  :placeholder="$t('classOperationV2.select')"
                  show-search
                  :disabled="!filters.academicStatusId || loadingAcademicYears"
                  :loading="loadingAcademicYears"
                  @change="onYearChange"
                >
                  <a-select-option v-for="year in academicYears" :key="year.value" :value="year.value">
                    {{ year.label }}
                  </a-select-option>
                </a-select>
              </div>
            </div>

            <!-- Academic Term with Load Button - Grid aligned with above -->
            <div class="ts-grid ts-grid-cols-1 md:ts-grid-cols-2 lg:ts-grid-cols-4 ts-gap-3 ts-mb-3">
              <!-- Academic Term - matches Main Program width -->
              <div class="ts-relative">
                <label class="ts-block ts-text-xs ts-font-medium ts-text-gray-700 ts-mb-1">
                  {{ $t('classOperationV2.academicTerm') }} <span class="ts-text-red-500">*</span>
                </label>
                <a-select
                  v-model="filters.academicTermId"
                  class="ts-w-full"
                  :placeholder="$t('classOperationV2.select')"
                  show-search
                  :disabled="!filters.academicYearId || loadingAcademicTerms"
                  :loading="loadingAcademicTerms"
                  @change="onTermChange"
                >
                  <a-select-option v-for="term in academicTerms" :key="term.value" :value="term.value">
                    {{ term.label }}
                  </a-select-option>
                </a-select>
              </div>

              <!-- Load Classes Button - matches Sub Program width -->
              <div class="ts-relative ts-flex ts-items-end">
                <a-button
                  type="primary"
                  class="ts-w-40"
                  :disabled="!filters.academicTermId"
                  @click="loadClasses"
                  :loading="loadingClasses"
                >
                  <a-icon type="search" />
                  {{ $t('classOperationV2.loadClasses') }}
                </a-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Classes List -->
        <div v-if="classes.length > 0" class="classes-section">
          <div class="ts-bg-blue-50 ts-border-l-4 ts-border-blue-400 ts-p-2 ts-mb-4 ts-rounded">
            <div class="ts-flex">
              <div class="ts-flex ts-items-center">
                <i class="fas fa-info-circle ts-text-blue-400"></i>
              </div>
              <div class="ts-ml-3">
                <p class="ts-text-sm ts-text-blue-700">
                  {{ $t('classOperationV2.batchCreationInfo') }}
                </p>
              </div>
            </div>
          </div>

          <div class="ts-flex ts-justify-between ts-items-center ts-mb-4">
            <h5 class="ts-text-base ts-font-semibold ts-text-gray-700">
              {{ $t('classOperationV2.foundClasses') }}: <span class="ts-text-blue-600">{{ classes.length }}</span>
            </h5>
            <div>
              <a-button type="dashed" @click="addNewClass" class="ts-mr-2">
                <a-icon type="plus" /> {{ $t('classOperationV2.addNewClass') }}
              </a-button>
              <a-button @click="expandAll" class="ts-mr-2">
                <a-icon type="down" /> {{ $t('classOperationV2.expandAll') }}
              </a-button>
              <a-button @click="collapseAll">
                <a-icon type="up" /> {{ $t('classOperationV2.collapseAll') }}
              </a-button>
            </div>
          </div>

          <!-- Class Table -->
          <div v-for="(classItem, index) in classes" :key="index" class="ts-mb-6">
            <div class="ts-border ts-border-gray-200 ts-rounded-lg ts-bg-white ts-shadow-sm ts-overflow-hidden">
              <!-- Table -->
              <div class="ts-overflow-x-auto">
                <table class="ts-w-full ts-table-fixed" style="min-width: 1400px;">
                  <thead>
                    <tr class="ts-bg-gray-400">
                      <th class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-left ts-border-r ts-border-gray-200" style="width: 60px;">
                        {{ $t('classOperationV2.status') }}
                      </th>
                      <th class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-left ts-border-r ts-border-gray-200" style="width: 100px;">{{ $t('classOperationV2.academicYear') }} <span class="ts-text-red-500">*</span></th>
                      <th class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-left ts-border-r ts-border-gray-200" style="width: 140px;">{{ $t('classOperationV2.academicTerm') }} <span class="ts-text-red-500">*</span></th>
                      <th class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-left ts-border-r ts-border-gray-200" style="width: 160px;">{{ $t('classOperationV2.subProgram') }} <span class="ts-text-red-500">*</span></th>
                      <th class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-left ts-border-r ts-border-gray-200" style="width: 100px;">{{ $t('classOperationV2.grade') }} <span class="ts-text-red-500">*</span></th>
                      <th class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-left ts-border-r ts-border-gray-200" style="width: 90px;">{{ $t('classOperationV2.category') }} <span class="ts-text-red-500">*</span></th>
                      <th class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-left ts-border-r ts-border-gray-200" style="width: 130px;">{{ $t('classOperationV2.className') }} <span class="ts-text-red-500">*</span></th>
                      <th class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-left ts-border-r ts-border-gray-200" style="width: 150px;">{{ $t('classOperationV2.teacher') }}</th>
                      <th class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-left ts-border-r ts-border-gray-200" style="width: 120px;">{{ $t('classOperationV2.building') }}</th>
                      <th class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-left ts-border-r ts-border-gray-200" style="width: 80px;">{{ $t('classOperationV2.room') }}</th>
                      <th class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-left ts-border-r ts-border-gray-200" style="width: 150px;">{{ $t('classOperationV2.studyTime') }} <span class="ts-text-red-500">*</span></th>
                      <th v-if="!classItem.old" class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-700 ts-text-center" style="width: 60px;">
                         {{ $t('actions') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Current Class Row -->
                    <tr v-if="classItem.old" class="ts-bg-blue-50 ts-border-b ts-border-gray-200">
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-gray-500 ts-border-r ts-border-gray-200">
                        <div class="cell-truncate">{{ $t('classOperationV2.current') }}</div>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-text-gray-800 ts-border-r ts-border-gray-200">
                        <a-tooltip :title="classItem.old.academicYear">
                          <div class="cell-truncate">{{ classItem.old.academicYear }}</div>
                        </a-tooltip>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-text-gray-800 ts-border-r ts-border-gray-200">
                        <a-tooltip :title="classItem.old.academicTerm">
                          <div class="cell-truncate">{{ classItem.old.academicTerm }}</div>
                        </a-tooltip>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-text-gray-800 ts-border-r ts-border-gray-200">
                        <a-tooltip :title="classItem.old.subProgram">
                          <div class="cell-truncate">{{ classItem.old.subProgram }}</div>
                        </a-tooltip>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-text-gray-800 ts-border-r ts-border-gray-200">
                        <a-tooltip :title="classItem.old.grade">
                          <div class="cell-truncate">{{ classItem.old.grade }}</div>
                        </a-tooltip>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-text-gray-800 ts-border-r ts-border-gray-200">
                        <a-tooltip :title="classItem.old.category || '-'">
                          <div class="cell-truncate">{{ classItem.old.category || '-' }}</div>
                        </a-tooltip>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-blue-600 ts-border-r ts-border-gray-200">
                        <a-tooltip :title="classItem.old.className">
                          <div class="cell-truncate">{{ classItem.old.className }}</div>
                        </a-tooltip>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-text-gray-800 ts-border-r ts-border-gray-200">
                        <a-tooltip :title="classItem.old.teacher || '-'">
                          <div class="cell-truncate">{{ classItem.old.teacher || '-' }}</div>
                        </a-tooltip>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-text-gray-800 ts-border-r ts-border-gray-200">
                        <a-tooltip :title="classItem.old.building || '-'">
                          <div class="cell-truncate">{{ classItem.old.building || '-' }}</div>
                        </a-tooltip>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-text-gray-800 ts-border-r ts-border-gray-200">
                        <a-tooltip :title="classItem.old.room || '-'">
                          <div class="cell-truncate">{{ classItem.old.room || '-' }}</div>
                        </a-tooltip>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-text-gray-800 ts-border-r ts-border-gray-200">
                        <a-tooltip :title="classItem.old.studyTime || '-'">
                          <div class="cell-truncate">{{ classItem.old.studyTime || '-' }}</div>
                        </a-tooltip>
                      </td>
                    </tr>
                    <!-- Next/New Class Row -->
                    <tr class="ts-bg-green-50 ts-border-b ts-border-gray-200">
                      <td class="ts-px-2 ts-py-2 ts-text-xs ts-font-semibold ts-text-green-600 ts-border-r ts-border-gray-200">
                        {{ classItem.old ? $t('classOperationV2.next') : $t('classOperationV2.new') }}
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-border-r ts-border-gray-200">
                        <a-select
                          v-model="classItem.new.academicYearId"
                          size="small"
                          show-search
                          :filter-option="filterOption"
                          style="width: 100%; font-size: 11px !important;"
                          :placeholder="$t('classOperationV2.select')"
                          :class="{ 'ant-select-error': errors.has(`classes.${index}.academic_year_id`) }"
                          :disabled="!!classItem.old"
                        >
                          <a-select-option v-for="year in academicYearsForRow" :key="year.value" :value="year.value">{{ year.label }}</a-select-option>
                        </a-select>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-border-r ts-border-gray-200">
                        <a-select
                          v-model="classItem.new.academicTermId"
                          size="small"
                          show-search
                          :filter-option="filterOption"
                          style="width: 100%; font-size: 11px !important;"
                          :placeholder="$t('classOperationV2.select')"
                          :class="{ 'ant-select-error': errors.has(`classes.${index}.academic_term_id`) }"
                          :disabled="!!classItem.old"
                        >
                          <a-select-option v-for="term in academicTermsForRow" :key="term.value" :value="term.value">{{ term.label }}</a-select-option>
                        </a-select>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-border-r ts-border-gray-200">
                        <a-select
                          v-model="classItem.new.subProgramId"
                          size="small"
                          show-search
                          :filter-option="filterOption"
                          style="width: 100%; font-size: 11px !important;"
                          :placeholder="$t('classOperationV2.select')"
                          :class="{ 'ant-select-error': errors.has(`classes.${index}.sub_program_id`) }"
                          disabled
                        >
                          <a-select-option v-for="program in subPrograms" :key="program.value" :value="program.value">{{ program.label }}</a-select-option>
                        </a-select>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-border-r ts-border-gray-200">
                        <a-select
                          v-model="classItem.new.gradeId"
                          size="small"
                          show-search
                          :filter-option="filterOption"
                          style="width: 100%; font-size: 11px !important;"
                          :placeholder="$t('classOperationV2.select')"
                          :class="{ 'ant-select-error': errors.has(`classes.${index}.grade_id`) }"
                          :disabled="!!classItem.old"
                          @change="(value) => onGradeChange(index, value)"
                        >
                          <a-select-option v-for="grade in grades" :key="grade.value" :value="grade.value">{{ grade.label }}</a-select-option>
                        </a-select>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-border-r ts-border-gray-200">
                        <a-select
                          v-model="classItem.new.categoryId"
                          size="small"
                          show-search
                          :filter-option="filterOption"
                          style="width: 100%; font-size: 11px !important;"
                          :placeholder="$t('classOperationV2.select')"
                          :class="{ 'ant-select-error': errors.has(`classes.${index}.ac_category_id`) }"
                          :disabled="!!classItem.old"
                        >
                          <a-select-option v-for="category in classCategories" :key="category.value" :value="category.value">{{ category.label }}</a-select-option>
                        </a-select>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-border-r ts-border-gray-200">
                        <a-input
                          v-model="classItem.new.className"
                          size="small"
                          style="height: 24px !important; font-size: 11px !important;"
                          :placeholder="$t('classOperationV2.enterClassName')"
                          :class="{ 'ant-input-error': errors.has(`classes.${index}.class_name`) }"
                        />
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-border-r ts-border-gray-200">
                        <a-select
                          v-model="classItem.new.teacherId"
                          size="small"
                          show-search
                          :filter-option="filterOption"
                          style="width: 100%; font-size: 11px !important;"
                          :placeholder="$t('classOperationV2.select')"
                          :class="{ 'ant-select-error': errors.has(`classes.${index}.teacher_id`) }"
                        >
                          <a-select-option v-for="teacher in teachers" :key="teacher.value" :value="teacher.value">{{ teacher.label }}</a-select-option>
                        </a-select>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-border-r ts-border-gray-200">
                        <a-select
                          v-model="classItem.new.buildingId"
                          size="small"
                          show-search
                          :filter-option="filterOption"
                          style="width: 100%; font-size: 11px !important;"
                          :placeholder="$t('classOperationV2.select')"
                          :class="{ 'ant-select-error': errors.has(`classes.${index}.building_id`) }"
                          @change="onBuildingChange(index)"
                        >
                          <a-select-option v-for="building in buildings" :key="building.value" :value="building.value">{{ building.label }}</a-select-option>
                        </a-select>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-border-r ts-border-gray-200">
                        <a-select
                          v-model="classItem.new.roomId"
                          size="small"
                          show-search
                          :filter-option="filterOption"
                          style="width: 100%; font-size: 11px !important;"
                          :placeholder="$t('classOperationV2.select')"
                          :class="{ 'ant-select-error': errors.has(`classes.${index}.room_id`) }"
                          :disabled="!classItem.new.buildingId"
                        >
                          <a-select-option v-for="room in getRoomsForBuilding(classItem.new.buildingId)" :key="room.value" :value="room.value">{{ room.label }}</a-select-option>
                        </a-select>
                      </td>
                      <td class="ts-px-2 ts-py-2 ts-border-r ts-border-gray-200">
                        <a-select
                          v-model="classItem.new.studyTimeId"
                          size="small"
                          show-search
                          :filter-option="filterOption"
                          style="width: 100%; font-size: 11px !important;"
                          :placeholder="$t('classOperationV2.select')"
                          :class="{ 'ant-select-error': errors.has(`classes.${index}.study_time_id`) }"
                        >
                          <a-select-option v-for="time in studyTimes" :key="time.value" :value="time.value">{{ time.label }}</a-select-option>
                        </a-select>
                      </td>
                      <td v-if="!classItem.old" class="ts-px-2 ts-py-2 ts-text-center">
                        <a-popconfirm
                          :title="$t('classOperationV2.confirmRemove')"
                          :ok-text="$t('classOperationV2.yes')"
                          :cancel-text="$t('classOperationV2.no')"
                          @confirm="removeClass(index)"
                          placement="topRight"
                        >
                          <a-button type="danger" size="small" icon="delete" v-tooltip="$t('classOperationV2.remove')" />
                        </a-popconfirm>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Student Section (only show if there's an old class reference) -->
              <div v-if="classItem.old" class="ts-border-t ts-border-gray-200 ts-bg-gray-50 ts-p-3">
                <div class="ts-flex ts-justify-between ts-items-center ts-mb-2">
                  <div class="ts-flex ts-items-center">
                    <a-button size="small" @click="toggleStudents(index)" type="link" class="ts-p-0">
                      <a-icon :type="classItem.studentsExpanded ? 'up' : 'down'" />
                      <i class="fas fa-users ts-ml-1 ts-mr-2"></i>
                      {{ $t('classOperationV2.students') }} ({{ classItem.students.length }})
                    </a-button>
                    <span class="ts-ml-3 ts-text-xs ts-text-gray-500 ts-bg-blue-100 ts-px-2 ts-py-1 ts-rounded">
                      {{ getSelectedStudentsCount(classItem) }} {{ $t('classOperationV2.selected') }}
                    </span>
                  </div>
                  <div v-if="classItem.studentsExpanded">
                    <a-button size="small" @click="selectAllStudents(index)" class="ts-mr-1">
                      {{ $t('classOperationV2.selectAll') }}
                    </a-button>
                    <a-button size="small" @click="deselectAllStudents(index)">
                      {{ $t('classOperationV2.deselectAll') }}
                    </a-button>
                  </div>
                </div>

                <div v-if="classItem.studentsExpanded">
                  <div class="ts-bg-yellow-50 ts-border-l-4 ts-border-yellow-400 ts-p-2 ts-mb-2 ts-rounded">
                    <p class="ts-text-xs ts-text-yellow-700">
                      {{ $t('classOperationV2.studentEnrollmentNote') }}
                    </p>
                  </div>
                  <div class="ts-max-h-48 ts-overflow-y-auto ts-bg-white ts-p-2 ts-rounded">
                    <div class="ts-grid ts-grid-cols-1 md:ts-grid-cols-2 lg:ts-grid-cols-4 ts-gap-2">
                      <div v-for="student in classItem.students" :key="student.id" class="ts-border ts-border-gray-200 ts-rounded ts-p-2 hover:ts-bg-blue-50 ts-transition">
                        <a-checkbox v-model="student.selected">
                          <span class="ts-text-xs ts-font-medium ts-text-gray-800">{{ student.name }}</span>
                          <span class="ts-text-xs ts-text-gray-500 ts-ml-1">({{ student.gender }})</span>
                        </a-checkbox>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-else-if="loadingClasses" class="ts-text-center ts-py-16">
          <a-spin size="large" />
          <p class="ts-text-gray-600 ts-text-base ts-mt-4 ts-mb-2 ts-font-medium">{{ $t('classOperationV2.loadingClasses') }}</p>
          <p class="ts-text-gray-400 ts-text-sm">{{ $t('classOperationV2.pleaseWait') }}</p>
        </div>

        <!-- Empty State -->
        <div v-else class="ts-text-center ts-py-16">
          <i class="fas fa-inbox ts-text-gray-300 ts-text-6xl ts-mb-4"></i>
          <p class="ts-text-gray-500 ts-text-base ts-mb-2">{{ $t('classOperationV2.noClassesLoaded') }}</p>
          <p class="ts-text-gray-400 ts-text-sm">{{ $t('classOperationV2.selectFiltersAndLoad') }}</p>
        </div>

        <!-- Actions -->
        <div v-if="classes.length > 0" class="ts-mt-6 ts-pt-4 ts-border-t ts-border-gray-200 ts-bg-gray-50 ts-p-4 ts-rounded">
          <div class="ts-flex ts-justify-between ts-items-center">
            <div>
              <span class="ts-inline-flex ts-items-center ts-px-3 ts-py-1 ts-rounded-full ts-text-sm ts-font-medium ts-bg-blue-100 ts-text-blue-800">
                {{ classes.length }} {{ $t('classOperationV2.classesToCreate') }}
              </span>
            </div>
            <div>
              <a-button @click="cancel" class="ts-mr-2">
                {{ $t('classOperationV2.cancel') }}
              </a-button>
              <a-button type="primary" @click="createBatch" :loading="saving">
                <a-icon type="save" /> {{ $t('classOperationV2.createBatch') }}
              </a-button>
            </div>
          </div>
        </div>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'

export default {
  name: 'CreateBatchClassOperation',
  data() {
    return {
      filters: {
        mainProgramId: null,
        subProgramId: null,
        academicStatusId: 1, // Default to Current
        academicYearId: null,
        academicTermId: null
      },
      initialLoading: false,
      loadingSubPrograms: false,
      loadingAcademicYears: false,
      loadingAcademicTerms: false,
      loadingClasses: false,
      saving: false,
      mainPrograms: [],
      subPrograms: [],
      academicTerms: [],
      academicYears: [],
      academicYearsForRow: [],
      teachers: [],
      buildings: [],
      rooms: [],
      studyTimes: [],
      classCategories: [],
      grades: [], // Shared grades for all classes (loaded once based on filter sub program)
      academicTermsForRow: [], // Shared terms for all rows (loaded once based on next term)
      classes: [],
      errors: new Errors()
    }
  },
  created() {
    this.loadInitialData()
  },
  methods: {
    filterOption(input, option) {
      return option.componentOptions.children[0].text
        .toLowerCase()
        .indexOf(input.toLowerCase()) >= 0
    },
    async onMainProgramChange() {
      this.filters.subProgramId = null
      this.filters.academicStatusId = 1 // Reset to Current
      this.filters.academicYearId = null
      this.filters.academicTermId = null
      this.classes = []
      await this.loadSubPrograms()
    },
    async onSubProgramChange() {
      this.filters.academicYearId = null
      this.filters.academicTermId = null
      this.classes = []
      this.grades = [] // Clear shared grades when sub program changes

      // Auto-load academic years for current status
      if (this.filters.academicStatusId) {
        await this.loadAcademicYears()

        // Auto-select first academic year if available (filtered by current status)
        if (this.academicYears.length > 0) {
          this.filters.academicYearId = this.academicYears[0].value
          await this.loadAcademicTerms()
        }
      }
    },
    async onStatusChange() {
      this.filters.academicYearId = null
      this.filters.academicTermId = null
      this.classes = []
      await this.loadAcademicYears()

      // Auto-select first academic year if available
      if (this.academicYears.length > 0) {
        this.filters.academicYearId = this.academicYears[0].value
        await this.loadAcademicTerms()
      }
    },
    async onYearChange() {
      this.filters.academicTermId = null
      this.classes = []
      await this.loadAcademicTerms()
    },
    onTermChange() {
      this.classes = []
    },
    async loadClasses() {
      this.loadingClasses = true
      try {
        const response = await this.$store.dispatch('classOperationV2/getClasses', {
          mainProgramId: this.filters.mainProgramId,
          subProgramId: this.filters.subProgramId,
          academicTermId: this.filters.academicTermId
        })
        this.classes = response.data.data || []

        // Load grades once for all classes (based on filter sub program)
        await this.loadGrades()

        // Load academic terms ONCE for all rows (if classes have auto-selected academicYearId)
        if (this.classes.length > 0 && this.classes[0].new.academicYearId) {
          await this.loadAcademicTermsForAllRows(this.classes[0].new.academicYearId)
        }

        // Auto-populate class names with grade names
        this.classes.forEach(classItem => {
          if (classItem.new.gradeId && !classItem.new.className) {
            const grade = this.grades.find(g => g.value === classItem.new.gradeId)
            if (grade) {
              classItem.new.className = grade.label
            }
          }
        })
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: (error.response && error.response.data && error.response.data.message) || 'Failed to load classes',
          style: { zIndex: 1031 }
        })
        this.classes = []
      } finally {
        this.loadingClasses = false
      }
    },
    async loadAcademicTermsForAllRows(academicYearId) {
      if (!academicYearId || !this.filters.subProgramId) return

      try {
        const response = await this.$store.dispatch('classOperationV2/getRowAcademicTerms', {
          academicYearId: academicYearId,
          subProgramId: this.filters.subProgramId
        })
        this.academicTermsForRow = response.data.data || []
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: 'Failed to load academic terms',
          style: { zIndex: 1031 }
        })
      }
    },
    async loadGrades() {
      if (!this.filters.subProgramId) return

      try {
        const response = await this.$store.dispatch('classOperationV2/getGrades', this.filters.subProgramId)
        this.grades = response.data.data || []
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: 'Failed to load grades',
          style: { zIndex: 1031 }
        })
      }
    },
    onBuildingChange(index) {
      this.classes[index].new.roomId = null
    },
    getTermsForYear(yearId) {
      if (!yearId) return []
      return this.academicTerms.filter(term => term.yearId === yearId)
    },
    getRoomsForBuilding(buildingId) {
      if (!buildingId) return []
      return this.rooms.filter(room => room.buildingId === buildingId)
    },
    toggleStudents(index) {
      this.classes[index].studentsExpanded = !this.classes[index].studentsExpanded
    },
    selectAllStudents(index) {
      this.classes[index].students.forEach(student => {
        student.selected = true
      })
    },
    deselectAllStudents(index) {
      this.classes[index].students.forEach(student => {
        student.selected = false
      })
    },
    getSelectedStudentsCount(classItem) {
      return classItem.students.filter(s => s.selected).length
    },
    expandAll() {
      this.classes.forEach(c => { c.studentsExpanded = true })
    },
    collapseAll() {
      this.classes.forEach(c => { c.studentsExpanded = false })
    },
    cancel() {
      this.$confirm({
        title: this.$t('classOperationV2.confirmCancel'),
        content: '',
        okText: this.$t('classOperationV2.yes'),
        cancelText: this.$t('classOperationV2.no'),
        onOk: () => {
          this.$router.push({ name: 'class-operation' })
        }
      })
    },
    async createBatch() {
      this.errors = new Errors()
      this.saving = true
      try {
        const payload = this.preparePayload()
        await this.$store.dispatch('classOperationV2/createBatch', payload)

        this.$notification.success({
          message: this.$t('classOperationV2.success'),
          description: this.$t('classOperationV2.batchCreatedSuccess'),
          style: { zIndex: 1031 }
        })
        this.$router.push({ name: 'class-operation' })
      } catch (error) {
        this.saving = false
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = new Errors(error.response.data.errors)
        }
        this.$notification.error({
          message: 'Error',
          description: (error.response && error.response.data && error.response.data.message) || error.message || 'Failed to create batch classes',
          style: { zIndex: 1031 }
        })
      } finally {
        this.saving = false
      }
    },
    preparePayload() {
      return {
        classes: this.classes.map(classItem => ({
          academic_year_id: classItem.new.academicYearId,
          academic_term_id: classItem.new.academicTermId,
          sub_program_id: classItem.new.subProgramId,
          grade_id: classItem.new.gradeId,
          ac_category_id: classItem.new.categoryId,
          class_name: classItem.new.className,
          teacher_id: classItem.new.teacherId,
          building_id: classItem.new.buildingId,
          room_id: classItem.new.roomId,
          study_time_id: classItem.new.studyTimeId,
          old_class_id: classItem.classId || null,
          students: classItem.students ? classItem.students.filter(s => s.selected).map(s => s.id) : []
        }))
      }
    },
    async loadInitialData() {
      this.initialLoading = true
      try {
        // Load all master data in parallel (grades are loaded per row based on sub program)
        const [mainPrograms, academicYearsForRow, teachers, buildings, rooms, studyTimes, classCategories] = await Promise.all([
          this.$store.dispatch('classOperationV2/getMainPrograms'),
          this.$store.dispatch('classOperationV2/getRowAcademicYears'),
          this.$store.dispatch('classOperationV2/getTeachers'),
          this.$store.dispatch('classOperationV2/getBuildings'),
          this.$store.dispatch('classOperationV2/getRooms'),
          this.$store.dispatch('classOperationV2/getStudyTimes'),
          this.$store.dispatch('classOperationV2/getClassCategories')
        ])

        this.mainPrograms = mainPrograms.data.data || []
        this.academicYearsForRow = academicYearsForRow.data.data || []
        this.teachers = teachers.data.data || []
        this.buildings = buildings.data.data || []
        this.rooms = rooms.data.data || []
        this.studyTimes = studyTimes.data.data || []
        this.classCategories = classCategories.data.data || []
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: 'Failed to load initial data',
          style: { zIndex: 1031 }
        })
      } finally {
        this.initialLoading = false
      }
    },
    async loadSubPrograms() {
      this.loadingSubPrograms = true
      try {
        const response = await this.$store.dispatch('classOperationV2/getSubPrograms', this.filters.mainProgramId)
        this.subPrograms = response.data.data || []
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: 'Failed to load sub programs',
          style: { zIndex: 1031 }
        })
        this.subPrograms = []
      } finally {
        this.loadingSubPrograms = false
      }
    },
    async loadAcademicYears() {
      this.loadingAcademicYears = true
      try {
        const response = await this.$store.dispatch('classOperationV2/getAcademicYears', this.filters.academicStatusId)
        this.academicYears = response.data.data || []
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: 'Failed to load academic years',
          style: { zIndex: 1031 }
        })
        this.academicYears = []
      } finally {
        this.loadingAcademicYears = false
      }
    },
    async loadAcademicTerms() {
      this.loadingAcademicTerms = true
      try {
        const response = await this.$store.dispatch('classOperationV2/getAcademicTerms', {
          academicYearId: this.filters.academicYearId,
          academicStatusId: this.filters.academicStatusId,
          subProgramId: this.filters.subProgramId
        })
        this.academicTerms = response.data.data || []
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: 'Failed to load academic terms',
          style: { zIndex: 1031 }
        })
        this.academicTerms = []
      } finally {
        this.loadingAcademicTerms = false
      }
    },
    addNewClass() {
      // Add a new blank class with no old reference
      this.classes.unshift({
        old: null, // No old class reference
        new: {
          academicYearId: null,
          academicTermId: null,
          subProgramId: this.filters.subProgramId, // Use filter's sub program (disabled in UI)
          gradeId: null, // User can manually select since there's no old grade reference
          categoryId: null, // User can manually select category
          className: '',
          teacherId: null,
          buildingId: null,
          roomId: null,
          studyTimeId: null
        },
        students: [], // No students for new blank class
        studentsExpanded: false
      })
      this.$notification.success({
        message: this.$t('classOperationV2.success'),
        description: this.$t('classOperationV2.addNewClass'),
        style: { zIndex: 1031 }
      })
    },
    onGradeChange(index, gradeId) {
      // Auto-populate class name with grade name
      const grade = this.grades.find(g => g.value === gradeId)
      if (grade) {
        this.classes[index].new.className = grade.label
      }
    },
    onBuildingChange(index) {
      // Reset room when building changes
      this.classes[index].new.roomId = null
    },
    removeClass(index) {
      this.classes.splice(index, 1)
    },
    goBackToList() {
      this.$router.push('/class-operations')
    }
  }
}
</script>

<style scoped>

/* Modern back button */
.back-button {
  border: 1px solid #e5e7eb !important;
  background-color: #ffffff !important;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
  transition: all 0.2s ease !important;
}

.back-button:hover {
  border-color: #3b82f6 !important;
  color: #3b82f6 !important;
  background-color: #eff6ff !important;
  transform: translateX(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
}

.back-button:active {
  transform: translateX(-3px);
}

/* Cell text overflow with tooltip */
.cell-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

/* Error border styles for validation */
.ant-select-error >>> .ant-select-selection,
.ant-select-error >>> .ant-select-selector {
  border-color: #ff4d4f !important;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
}

.ant-input-error,
.ant-input-error:focus {
  border-color: #ff4d4f !important;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
}

/* Center all Ant Design icons horizontally */
.anticon {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  vertical-align: middle !important;
}
</style>
